
import Vue from 'vue';
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';
import SkeletonLoader from '@/components/SkeletonLoader.vue';
import IconButton from '@/components/IconButton.vue';
import LabeledTextWithContext from '@/components/LabeledTextWithContext.vue';
import LabelPickerAdvancedController, { currentLabelStatus } from '@/controllers/LabelPickerAdvancedController';
import LabelPickerAdvancedSelectedLabels from '@/components/LabelPickerAdvancedSelectedLabels.vue';
import LabelPickerAdvancedDocumentLabels from '@/components/LabelPickerAdvancedDocumentLabels.vue';
import {
  ChatMessage, Clause, ExpandedFragment, ExpandedFragmentBox, IngestTextFragment, Label, LabelAssignment, LabelPickerAdvancedLabelStatus,
  MlLabelSuggestionItem, MlThemePrediction, SemanticSearchResult, SelectionQuery, Time,
} from '@/model';
import deepEqual from '@/util/deep-equals';
import api from '@/rest-api';
import deepClone from '@/util/deep-clone';
import { distinct, notEmpty, sortBy } from '@/util/array-util';
import users from '@/store/users';
import labels, { labelSorter } from '@/store/labels';
import RelatedFragments from '@/components/RelatedFragments.vue';
import HtmlTable from '@/components/HtmlTable.vue';
import ChatPanel from '@/components/ChatPanel.vue';
import { notifyError } from '@/store/notification';

export default Vue.extend({
  name: 'FragmentsEditor',
  components: {
    Splitpanes,
    Pane,
    LabeledTextWithContext,
    SkeletonLoader,
    IconButton,
    LabelPickerAdvancedController,
    LabelPickerAdvancedSelectedLabels,
    LabelPickerAdvancedDocumentLabels,
    RelatedFragments,
    HtmlTable,
    ChatPanel,
  },
  props: {
    fragmentIds: { type: Array as () => number[], required: true },
    newFragment: { type: Object as () => IngestTextFragment | undefined, required: false },
    autoselect: { type: Boolean, default: true },
    showSemanticSearch: { type: Boolean, default: true },
    showAssistant: { type: Boolean, default: false },
  },

  data() {
    return {
      selectedFragment: undefined as undefined | ExpandedFragment,
      initialized: false,
      loadingFragmentIds: [] as number[],
      fragments: [] as ExpandedFragment[],
      actualNewFragment: undefined as undefined | IngestTextFragment,
      relatedFragments: [] as SemanticSearchResult[],
      rawMlSuggestions: [] as readonly MlLabelSuggestionItem[],
      mlPredictedThemes: [] as readonly MlThemePrediction[],
      isLoadingPotential: false,
      chatHistory: [] as ChatMessage[],
      chatLoading: false,
      showOriginalBoxIds: [] as number[],
      originalTexts: {} as Record<number, string>,
      paneSizes2: [40, 60],
      paneSizes3: [40, 45, 15],
      paneSizes3assistant: [35, 35, 30],
      paneSizes4: [25, 30, 15, 30],
    };
  },

  watch: {
    showAssistant(shown: boolean) {
      if (shown) {
        this.$nextTick(() => {
          (this.$refs.assistantChat as any)?.focusInput?.();
        });
      }
    },
    newFragment: {
      immediate: true,
      handler(to: IngestTextFragment | undefined) {
        if (!deepEqual(to, this.actualNewFragment)) {
          this.actualNewFragment = to;
        }
      },
    },
    fragmentIds: {
      immediate: true,
      async handler(to: number[], from) {
        this.initialized = false;
        if (!!to && !deepEqual(to, from)) {
          this.selectedFragment = undefined;
          this.chatHistory = [];
          this.loadingFragmentIds = [...to];
          // Keep any not-yet-saved new fragment (id 0), it is expanded and selected by the
          // actualNewFragment watcher, which fires independently for the same change.
          const newFragment = this.fragments.filter((f) => f.fragment.id === 0);
          this.fragments = [
            ...newFragment,
            ...await api.expandedFragmentsById(to),
          ];
          if (this.autoselect && !this.actualNewFragment && this.fragments.length === 1) {
            this.selectedFragment = deepClone(this.fragments[0]);
          }
          this.fragments.sort((a, b) => {
            if (a.fragment.created === b.fragment.created) {
              return 0;
            }
            if (!a.fragment.created) {
              return 1;
            }
            if (!b.fragment.created) {
              return -1;
            }
            return a.fragment.created - b.fragment.created;
          });

          this.loadingFragmentIds = [];
        }
        this.initialized = true;
      },
    },
    actualNewFragment: {
      async handler(to: IngestTextFragment | undefined) {
        if (!to) return;
        if (to.id !== 0) {
          throw new Error('New fragment cannot have an id != 0');
        }
        this.loadingFragmentIds = [0];
        this.fragments = [
          ...this.fragments,
          ...await api.expandedFragments([to]),
        ];
        this.loadingFragmentIds = [];
        this.selectedFragment = deepClone(this.fragments.find((f) => f.fragment.id === 0));
        // scroll to bottom
        this.scrollToBottom();
      },
    },
    selectedFragment: {
      deep: true,
      async handler(from: ExpandedFragment | undefined, to: ExpandedFragment | undefined) {
        this.$emit('hasChanges', this.hasChanges());
        // if new id

        if (to?.fragment.id !== from?.fragment.id || !from) {
          this.relatedFragments = [];
          const documentId = this.selectedFragment?.documentId ?? 0;
          // Dispatch the ml label-suggestion request first (before semantic search) so
          // maia-ml processes it with priority; otherwise the two requests race and block
          // each other.
          const mlSuggestionsPromise = this.loadMlLabelSuggestions(this.relevantText);
          try {
            this.relatedFragments = (await api.semanticSearchExpand({
              query: this.relevantText,
              limit: 10,
              filters: [{
                field: 'documentId',
                values: [documentId],
                negate: true,
              }],
            }))
              .filter((f) => !this.selectedFragment?.boxes.some((b) => b.boxId === f.item.id));
          } catch {
            this.relatedFragments = [];
            notifyError('Could not load related fragments');
          }
          await mlSuggestionsPromise;
        }
      },
    },
  },
  computed: {
    selectedFragmentLabelStatus(): LabelPickerAdvancedLabelStatus {
      const originalLabels = this.fragments.find((frag) => frag.fragment.id === this.selectedFragment?.fragment.id)?.fragment.labels ?? [];
      const currentLabels = this.selectedFragment?.fragment.labels ?? [];
      return currentLabelStatus(
        originalLabels,
        currentLabels,
      );
    },
    allSameDocumentLabels(): boolean {
      const all = this.fragments
        .map((f) => JSON.stringify(f.documentLabels))
        .filter((value, index, array) => array.indexOf(value) === index);
      return all.length === 1;
    },
    relevantText(): string {
      return this.selectedFragment?.boxes
        .map((b) => b.text)
        .join(' ') ?? '';
    },
    currentPaneSizes(): number[] {
      if (this.showSemanticSearch && this.showAssistant) return this.paneSizes4;
      if (this.showAssistant) return this.paneSizes3assistant;
      if (this.showSemanticSearch) return this.paneSizes3;
      return this.paneSizes2;
    },
    assistantQuery(): SelectionQuery {
      const clauses: Clause[] = [];
      const editedIds = this.fragmentIds.filter((id) => id > 0);
      if (editedIds.length > 0) {
        clauses.push({ type: 'in', field: 'fragment', values: editedIds });
      }
      if (this.showSemanticSearch && this.relatedFragments.length > 0) {
        clauses.push({ type: 'in', field: 'box', values: this.relatedFragments.map((f) => f.item.id) });
      }
      if (clauses.length === 0) return { clause: { type: 'and', clauses: [] } };
      if (clauses.length === 1) return { clause: clauses[0] };
      return { clause: { type: 'or', clauses } };
    },
  },
  mounted() {
    const sizes = this.loadPaneSizes();
    this.paneSizes2 = sizes.paneSizes2;
    this.paneSizes3 = sizes.paneSizes3;
    this.paneSizes3assistant = sizes.paneSizes3assistant;
    this.paneSizes4 = sizes.paneSizes4;
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    async loadMlLabelSuggestions(text: string) {
      if (!text) {
        this.rawMlSuggestions = [];
        this.mlPredictedThemes = [];
        return;
      }
      this.isLoadingPotential = true;
      try {
        const result = await api.mlLabelSuggestions({ text, topK: 5 });
        this.rawMlSuggestions = result.suggestions;
        this.mlPredictedThemes = result.themes;
      } catch {
        this.rawMlSuggestions = [];
        this.mlPredictedThemes = [];
        notifyError('Could not load label suggestions');
      } finally {
        this.isLoadingPotential = false;
      }
    },
    isHtmlTable(text: string): boolean {
      return !!text && text.trimStart().toLowerCase().startsWith('<table');
    },
    async toggleOriginal(box: ExpandedFragmentBox) {
      if (this.showOriginalBoxIds.includes(box.boxId)) {
        this.showOriginalBoxIds = this.showOriginalBoxIds.filter((id) => id !== box.boxId);
        return;
      }
      this.showOriginalBoxIds = [...this.showOriginalBoxIds, box.boxId];
      if (this.originalTexts[box.boxId] === undefined) {
        this.originalTexts = {
          ...this.originalTexts,
          [box.boxId]: (await api.originalBoxText(box.boxId)).text,
        };
      }
    },
    handleKeydown(e: KeyboardEvent) {
      // ctrl + s -> save the selected fragment
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (this.selectedFragment && this.hasChanges()) {
          this.saveFragment(this.selectedFragment.fragment as IngestTextFragment);
        }
      }
    },
    updateTimeAndUserOfLabelAssignment(la: LabelAssignment) {
      const time = la.deleted ?? la.time;
      const user = la.deletedBy ?? la.userId;
      return {
        time: time ? Time.asString(time) : 'unknown',
        user: user ? users.getters.lookup.get(user)?.displayName ?? `unknown user ${user}` : 'unknown',
      };
    },
    lastChangedForFragment(fragment: IngestTextFragment): { time: string; user: string } | undefined {
      const a = sortBy((la: LabelAssignment) => la.deleted ?? la.time ?? 0, true)([
        ...fragment.labels.filter((la) => la.time),
        ...(fragment.removedLabels ?? []).filter((rla) => rla.deleted),
      ]);
      return a
        .map((la) => this.updateTimeAndUserOfLabelAssignment(la))[0];
    },
    isSelectedFragment(fragmentId: number): boolean {
      return this.selectedFragment?.fragment.id === fragmentId;
    },
    isLoading(fragmentId: number): boolean {
      return this.loadingFragmentIds.includes(fragmentId);
    },
    selectFragment(fragment: ExpandedFragment | undefined) {
      this.confirmDiscardChanges(() => {
        this.selectedFragment = fragment !== undefined ? deepClone(fragment) : undefined;
      });
    },
    async saveFragment(fragment: IngestTextFragment) {
      this.loadingFragmentIds.push(fragment.id);
      if (fragment.id === 0) {
        this.actualNewFragment = undefined;
      }
      const saved = await api.saveFragment(fragment);
      const savedExpanded = (await api.expandedFragmentsById([saved.id]))[0];
      const i = this.fragments.findIndex((frag) => frag.fragment.id === fragment.id);
      if (i >= 0) {
        Vue.set(this.fragments, i, savedExpanded);
      } else {
        this.fragments.push(savedExpanded);
      }
      this.loadingFragmentIds = this.loadingFragmentIds.filter((id) => id !== fragment.id);
      this.selectedFragment = deepClone(savedExpanded);
      await this.reloadExpandedFragments();
      this.$emit('fragmentSaved', saved);
    },
    async deleteFragment(fragment: IngestTextFragment) {
      // eslint-disable-next-line no-alert
      if (window.confirm('Permanently delete fragment?')) {
        if (fragment.id === 0) {
          this.actualNewFragment = undefined;
        } else {
          await api.deleteFragment(fragment.id);
        }
        this.selectedFragment = undefined;
        const i = this.fragments.findIndex((frag) => frag.fragment.id === fragment.id);
        if (i >= 0) {
          Vue.delete(this.fragments, i);
        }
        await this.reloadExpandedFragments();
        this.$emit('fragmentDeleted', fragment);
      }
    },
    async reloadExpandedFragments() {
      const fragmentIds = this.fragments.map((f) => f.fragment.id);
      this.loadingFragmentIds = [...fragmentIds];
      this.fragments = [
        ...await api.expandedFragmentsById(fragmentIds),
      ];
      this.loadingFragmentIds = [];
    },
    hasChanges(): boolean {
      const edit = this.selectedFragment?.fragment;
      if (!edit) {
        return false;
      } else {
        const existing = this.fragments.find((frag) => frag.fragment.id === edit.id)?.fragment;

        const editLabelIds = [...edit.labels.map((la) => la.labelId)].sort((a, b) => a - b);
        const existingLabelIds = edit.id > 0 ? [...existing?.labels.map((la) => la.labelId) ?? []].sort((a, b) => a - b) : [];
        const isolateChanged = existing?.isolate !== edit.isolate;
        return !deepEqual(editLabelIds, existingLabelIds) || (existing?.description ?? '') !== (edit.description ?? '') || isolateChanged;
      }
    },
    confirmDiscardChanges(f: () => void) {
      // eslint-disable-next-line no-alert
      if (!this.hasChanges() || window.confirm('Discard unsaved changes?')) {
        f();
      }
    },
    chatRefClick({ type, id }: { type: string; id: number }) {
      if (type === 'label') {
        const label = labels.getters.lookup.get(id);
        if (label) this.labelClick(label);
      }
    },
    labelClick(label: Label) {
      // add label to selected fragment
      const selected = this.selectedFragment?.fragment;
      if (this.selectedFragment && selected) {
        const existing = selected.labels.find((la) => la.labelId === label.id);
        if (!existing) {
          // add label
          this.selectedFragment = {
            ...this.selectedFragment,
            fragment: {
              ...selected,
              labels: [...selected.labels, { labelId: label.id, suggestions: [{ type: 'ingest' }] }],
            },
          };
        }
      }
    },
    duplicateFragment(fragment: IngestTextFragment) {
      this.actualNewFragment = {
        ...fragment,
        id: 0,
        labels: fragment.labels.map((l) => ({ labelId: l.labelId })),
        removedLabels: undefined,
        isolate: true,
        created: Time.now(),
      };
    },
    fragmnetLabels(fragments: IngestTextFragment[]): Label[] {
      const allLabels = distinct(fragments.flatMap((f) => f.labels).map((l) => l.labelId));
      return allLabels
        .map((id) => labels.getters.lookup.get(id))
        .filter(notEmpty)
        .sort(labelSorter(['type', 'length', 'alpha']));
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.left');
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    },

    async executeChat(chat: string) {
      const userMessage: ChatMessage = { role: 'user', content: chat };
      const historyWithUser = [...this.chatHistory, userMessage];
      this.chatHistory = [...historyWithUser, { role: 'assistant', content: '' }];
      this.chatLoading = true;
      let hasTokens = false;
      try {
        await api.queryChatStream(
          this.assistantQuery,
          historyWithUser,
          (token: string) => {
            const lastIndex = this.chatHistory.length - 1;
            if (!hasTokens) {
              hasTokens = true;
              this.chatHistory = [...this.chatHistory.slice(0, lastIndex), { ...this.chatHistory[lastIndex], content: '' }];
            }
            const updated: ChatMessage = { ...this.chatHistory[lastIndex], content: this.chatHistory[lastIndex].content + token };
            this.chatHistory = [...this.chatHistory.slice(0, lastIndex), updated];
          },
          (status: string) => {
            if (!hasTokens) {
              const lastIndex = this.chatHistory.length - 1;
              this.chatHistory = [...this.chatHistory.slice(0, lastIndex), { ...this.chatHistory[lastIndex], content: status }];
            }
          },
        );
      } catch {
        const lastIndex = this.chatHistory.length - 1;
        this.chatHistory = [...this.chatHistory.slice(0, lastIndex), { role: 'assistant', content: 'The request timed out. Please try again.' }];
      } finally {
        this.chatLoading = false;
      }
    },

    loadPaneSizes(): { paneSizes2: number[], paneSizes3: number[], paneSizes3assistant: number[], paneSizes4: number[] } {
      const saved2 = localStorage.getItem('fragments-editor-pane-sizes-2');
      const saved3 = localStorage.getItem('fragments-editor-pane-sizes-3');
      const saved3a = localStorage.getItem('fragments-editor-pane-sizes-3a');
      const saved4 = localStorage.getItem('fragments-editor-pane-sizes-4');
      return {
        paneSizes2: saved2 ? JSON.parse(saved2) : [40, 60],
        paneSizes3: saved3 ? JSON.parse(saved3) : [40, 45, 15],
        paneSizes3assistant: saved3a ? JSON.parse(saved3a) : [35, 35, 30],
        paneSizes4: saved4 ? JSON.parse(saved4) : [25, 30, 15, 30],
      };
    },

    savePaneSizes() {
      localStorage.setItem('fragments-editor-pane-sizes-2', JSON.stringify(this.paneSizes2));
      localStorage.setItem('fragments-editor-pane-sizes-3', JSON.stringify(this.paneSizes3));
      localStorage.setItem('fragments-editor-pane-sizes-3a', JSON.stringify(this.paneSizes3assistant));
      localStorage.setItem('fragments-editor-pane-sizes-4', JSON.stringify(this.paneSizes4));
    },

    onPaneResize(event: Array<{ size: number }>) {
      const sizes = event.map((pane) => pane.size);
      if (this.showSemanticSearch && this.showAssistant) {
        this.paneSizes4 = sizes;
      } else if (this.showAssistant) {
        this.paneSizes3assistant = sizes;
      } else if (this.showSemanticSearch) {
        this.paneSizes3 = sizes;
      } else {
        this.paneSizes2 = sizes;
      }
      this.savePaneSizes();
    },
  },
});
